/* @SH: Has customisations */

import { config } from '@archetype-themes/scripts/config'
import Photoswipe from '@archetype-themes/scripts/modules/photoswipe'
import { Slideshow } from '@archetype-themes/scripts/modules/slideshow'
import YouTube from '@archetype-themes/scripts/helpers/youtube'
import VimeoPlayer from '@archetype-themes/scripts/helpers/vimeo'
/* @SH: Added publish import */
import { EVENTS, subscribe, publish } from '@archetype-themes/utils/pubsub'
/* /@SH */
import videoModal from '@archetype-themes/scripts/modules/video-modal'
import { init, removeSectionModels } from '@archetype-themes/scripts/modules/product-media'

/* @SH: Added debounce */
import { debounce, throttle } from '@archetype-themes/scripts/helpers/utils'
/* /@SH */

class ProductImages extends HTMLElement {
  constructor() {
    super()

    this.container = this

    if (!!this.container.dataset.modal) {
      this.init()
    }
  }

  init() {
    this.videoObjects = {}

    this.classes = {
      hidden: 'hide'
    }

    this.selectors = {
      productVideo: '.product__video',
      videoParent: '.product__video-wrapper',
      slide: '.product-main-slide',
      currentSlide: '.is-selected',
      startingSlide: '.starting-slide',

      currentVariantJson: '[data-current-variant-json]',
      productOptionsJson: '[data-product-options-json]',

      media: '[data-product-media-type-model]',
      closeMedia: '.product-single__close-media',
      thumbSlider: '[data-product-thumbs]',
      thumbScroller: '.product__thumbs--scroller',
      mainSlider: '[data-product-photos]',
      imageContainer: '[data-product-images]',
      /* @SH: Added selector */
      siteHeader: '.site-header',
      stickyColumn: '.product-single__sticky',
      /* /@SH */
    }

    this.sectionId = this.container.getAttribute('data-section-id')

    this.settings = {
      imageSetName: null,
      imageSetIndex: null,
      currentImageSet: null,
      currentSlideIndex: 0,
      mediaGalleryLayout: this.dataset.mediaGalleryLayout,
      hasVideos: this.container.querySelector(this.selectors.productVideo) ? true : false,
      hasModels: this.container.querySelector('[data-product-media-type-model]') ? true : false,
      /* @SH: Added setting */
      shThumbsArrowBehaviour: this.dataset.thumbsArrowBehaviour
      /* /@SH */
    }

    this.currentVariant = JSON.parse(this.querySelector(this.selectors.currentVariantJson).textContent)
    this.productOptions = JSON.parse(this.querySelector(this.selectors.productOptionsJson).textContent)

    this.cacheElements()

    const dataSetEl = this.cache.mainSlider.querySelector('[data-set-name]')
    if (dataSetEl) {
      this.settings.imageSetName = dataSetEl.dataset.setName
      this.settings.imageSetIndex =
        'option' + (this.productOptions.findIndex((opt) => this.getImageSetName(opt) == this.settings.imageSetName) + 1)
    }

    this.initVariants()
    this.initImageZoom()
    this.initModelViewerLibraries()
    this.videoSetup()
    this.initProductSlider(this.currentVariant)
    this.customMediaListeners()
    // open youtube/vimeo/mp4 video in modal
    videoModal(this)

    /* @SH: Handling for thumb groups click and filtering */
    if (document.querySelectorAll('.sh-thumb-groups').length) {
      this.thumbGroupChangeUnsbuscribe = subscribe(EVENTS.shThumbGroupClick, this.shOnThumbGroup.bind(this));
    }
    /* /@SH */

    /* @SH: Handling for sticky images */
    this.shDebounceStickyCalc = debounce(500, this.shSetDesktopStickyPosition.bind(this));
    document.addEventListener('headerStickyChange', this.shDebounceStickyCalc);
    /* /@SH */

    /* @SH: Image gallery init event */
    document.dispatchEvent(new CustomEvent('product-images:init', {detail: { productImages: this }}));
    /* /@SH */
  }

  /* @SH: Sticky position for desktop columns */
  shSetDesktopStickyPosition() {
    let headerCurrentHeight = theme.config.stickyHeader ? this.shOuterHeight(document.querySelector(this.selectors.siteHeader)) : 0;
    document.querySelectorAll(this.selectors.stickyColumn).forEach((el) => {
      el.style.top = `${Math.round(headerCurrentHeight)}px`;
    });
  }
  /* /@SH */

  /* @SH: Sticky position for desktop columns */
  shSetMobileStickyImage() {
    const photos = document.querySelector('.product__main-photos');
    let wrapper = null;

    if (theme.config.bpSmall) {  // Small screen
      window.removeEventListener('scroll', this.shStickyMobileImageScrollHandler);
      this.shStickyMobileImageScrollHandler = throttle(15, this.shStickyMobileImageScroll.bind(this));
      window.addEventListener('scroll', this.shStickyMobileImageScrollHandler);

      if (!this.photoWrapped) {
        wrapper = document.createElement('div');
        wrapper.setAttribute('data-sticky-mobile-wrap', '');
        photos.parentNode.insertBefore(wrapper, photos);
        wrapper.appendChild(photos);
        this.photoWrapped = true;
      }
    } else {  // Large screen
      window.removeEventListener('scroll', this.shStickyMobileImageScrollHandler);

      if (this.photoWrapped) {
        wrapper = photos.parentNode;
        if (wrapper && wrapper.hasAttribute('data-sticky-mobile-wrap')) {
          wrapper.parentNode.insertBefore(photos, wrapper);
          wrapper.parentNode.removeChild(wrapper);
        }
        this.photoWrapped = false;
      }
    }
  }

  shStickyMobileImageScroll() {
    let headerCurrentHeight = theme.config.stickyHeader ? this.shOuterHeight(document.querySelector(this.selectors.siteHeader)) : 0;

    const photosWrap = document.querySelector('[data-sticky-mobile-wrap]');
    const photos = document.querySelector('.product__main-photos');
    let formConfigure = document.querySelector('.sh-block-wrapper');
    // If not using a wrapper block, fallback to the meta column
    if (!formConfigure) {
      formConfigure = document.querySelector('.product-single__meta');
    }

    const windowScrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const windowHeight = window.innerHeight;

    // Calculate when the image should be sticky.
    const photosWrapOffsetTop = photosWrap.getBoundingClientRect().top + windowScrollTop;
    const stuck = (photosWrapOffsetTop + this.shMobileImagesHeight - windowScrollTop) <= headerCurrentHeight + (photosWrap.offsetHeight * 0.3);

    // Toggle the heights, top and classes depending on if it is stuck or not.
    photos.style.top = stuck ? `${headerCurrentHeight - 2}px` : '';
    photos.classList.toggle('is-stuck', stuck);

    clearTimeout(this.wrapSize);
    if (stuck) {
      photosWrap.style.height = `${this.shMobileImagesHeight}px`;
    } else {
      this.wrapSize = setTimeout(() => {
        photosWrap.style.height = 'auto';
      }, 500);
    }

    setTimeout(() => {
      // in timeout to get the animation of the image moving down.
      photos.classList.toggle('is-stuck--opening', stuck);
      this.shToggleAdaptiveImageSlider(!stuck);
    }, 50);

    if (stuck && formConfigure) {
      // Move the image back up when scrolled down a fair way on the configuration form - so the image doesn't obstruct the rest of the page's info.
      const formConfigureOffsetTop = formConfigure.getBoundingClientRect().top + windowScrollTop;
      const unstickBottom = (formConfigureOffsetTop + formConfigure.offsetHeight - windowScrollTop) <= (windowHeight * 0.5);
      photos.classList.toggle('is-stuck--closing', unstickBottom);
    }
  }

  shToggleAdaptiveImageSlider(adaptiveHeight) {
    if (this.flickity) {
      this.flickity.slideshow.options.adaptiveHeight = adaptiveHeight;
      this.flickity.slideshow.resize();
    }
  }
  /* /@SH */

  /* @SH: Helper function */
  shOuterHeight(el) {
    let height = el.offsetHeight;
    const style = getComputedStyle(el);

    height += parseInt(style.marginTop) + parseInt(style.marginBottom);
    return height;
  }
  /* /@SH */

  disconnectedCallback() {
    removeSectionModels(this.sectionId)

    if (this.flickity && typeof this.flickity.destroy === 'function') {
      this.flickity.destroy()
    }

    /* @SH: Remove thumbgroup click handler */
    if (typeof this.thumbGroupChangeUnsbuscribe === 'function') {
      this.thumbGroupChangeUnsbuscribe();
    }
    /* /@SH */

    /* @SH: Handling for sticky images */
    document.removeEventListener('headerStickyChange', this.shDebounceStickyCalc);
    window.removeEventListener('resize', this.shDebounceSetMobileStickyImage);
    /* /@SH */
  }

  cacheElements() {
    this.cache = {
      mainSlider: this.container.querySelector(this.selectors.mainSlider),
      thumbSlider: this.container.querySelector(this.selectors.thumbSlider),
      thumbScroller: this.container.querySelector(this.selectors.thumbScroller)
    }
  }

  initVariants() {
    subscribe(`${EVENTS.variantChange}:${this.dataset.productId}`, this.updateVariantImage.bind(this))
    // image set names variant change listeners
    if (this.settings.imageSetIndex)
      subscribe(`${EVENTS.variantChange}:${this.dataset.productId}`, this.updateImageSet.bind(this))
  }

  /*============================================================================
    Variant change methods
  ==============================================================================*/
  imageSetArguments(variant) {
    variant = variant ? variant : this.variants ? this.variants.currentVariant : null
    if (!variant) return

    const setValue = (this.settings.currentImageSet = this.getImageSetName(variant[this.settings.imageSetIndex]))
    const set = `${this.settings.imageSetName}_${setValue}`

    // Always start on index 0
    this.settings.currentSlideIndex = 0

    // Return object that adds cellSelector to mainSliderArgs
    return {
      cellSelector: '[data-group="' + set + '"]',
      imageSet: set,
      initialIndex: this.settings.currentSlideIndex
    }
  }

  /* @SH: added params for thumb groups to hijack imageSet logic */
  updateImageSet(evt, shThumbGroups, shThumbGroup) {
  /* /@SH */
    // If called directly, use current variant
    const variant = evt ? evt.detail.variant : this.variants ? this.variants.currentVariant : null

    /* @SH: added condition for thumb groups to hijack imageSet logic */
    if (!variant && !shThumbGroups) {
    /* /@SH */
      return
    }

    /* @SH: added condition for thumb groups to hijack imageSet logic */
    const setValue = shThumbGroups ? shThumbGroup : this.getImageSetName(variant[this.settings.imageSetIndex])
    /* /@SH */

    // Already on the current image group
    if (this.settings.currentImageSet === setValue) {
      return
    }

    /* @SH: added params for thumb groups to hijack imageSet logic */
    this.initProductSlider(variant, shThumbGroups, shThumbGroup)
    /* /@SH */
    /**
     * @event product-images:updateImageSet
     * @description Triggered when the image set is updated.
     */
    document.dispatchEvent(new CustomEvent('product-images:updateImageSet'))
  }

  // Show/hide thumbnails based on current image set
  /* @SH: added params for thumb groups to hijack imageSet logic */
  updateImageSetThumbs(set, shThumbGroups) {
  /* /@SH */
    this.cache.thumbSlider.querySelectorAll('.product__thumb-item').forEach((thumb) => {
      /* @SH: added condition for thumb groups to hijack imageSet logic */
      thumb.classList.toggle(this.classes.hidden, shThumbGroups ? thumb.dataset.thumbGroup !== set : thumb.dataset.group !== set)
      /* /@SH */
    });

    /* @SH: Trigger scroll to show/hide thumb arrows as needed */
    this.cache.thumbScroller.dispatchEvent(new Event('scroll'));
    /* /@SH */
  }

  getImageSetName(string) {
    return string
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/-$/, '')
      .replace(/^-/, '')
  }

  /*============================================================================
    Product images
  ==============================================================================*/
  initImageZoom() {
    const container = this.container
    if (!container) {
      return
    }
    this.photoswipe = new Photoswipe(this, this.sectionId)
    container.addEventListener(
      'photoswipe:afterChange',
      function (evt) {
        if (this.flickity) {
          this.flickity.goToSlide(evt.detail.index)
        }
      }.bind(this)
    )
    // Execute JS modules after the tooltip is opened
    document.addEventListener('tooltip:open', (e) => {
      if (!e.detail.context === 'QuickShop') return
      const scripts = document.querySelectorAll('tool-tip product-component script[type="module"]')
      for (let i = 0; i < scripts.length; i++) {
        let script = document.createElement('script')
        script.type = 'module'
        script.textContent = scripts[i].textContent
        scripts[i].parentNode.replaceChild(script, scripts[i])
      }
    })
  }

  getThumbIndex(target) {
    /* @SH: Added thumb group handling for variant image switch */
    if (this.settings.shThumbGroup) {
      const parentThumbEl = target.closest('.product__thumb-item');

      // Switch to that thumb group that contains the variant image
      if (parentThumbEl.dataset.thumbGroup !== this.settings.shThumbGroup) {
        this.flickity.goToSlide(0);
        this.settings.shThumbGroup = parentThumbEl.dataset.thumbGroup;
        this.updateImageSet(null, true, parentThumbEl.dataset.thumbGroup);

        publish(EVENTS.shThumbGroupRequest, {
          detail: {
            thumbGroup: parentThumbEl.dataset.thumbGroup
          }
        });
      }

      const visibleThumbs = Array.from(parentThumbEl.parentElement.querySelectorAll('.product__thumb-item:not(.hide)'));
      return visibleThumbs.indexOf(parentThumbEl);
    } else {
    /* /@SH */

    return target.dataset.index

    /* @SH: Added thumb group handling for variant image switch */
    }
    /* /@SH */
  }

  updateVariantImage(evt) {
    const variant = evt?.detail?.variant

    if (!variant || !variant.featured_media) return
    if (!config.bpSmall && this.settings.mediaGalleryLayout === 'stacked') {
      const slide = this.cache.mainSlider.querySelector(
        `.product-main-slide[data-media-id="${variant.featured_media.id}"]`
      )

      const imageIndex = this.getThumbIndex(slide)

      this.scrollToStackedMedia(imageIndex)

      this.handleStackedMediaChange(imageIndex)
    } else {
      const newImage = this.container.querySelector('.product__thumb[data-id="' + variant.featured_media.id + '"]')
      const imageIndex = this.getThumbIndex(newImage)

      // If there is no index, slider is not initalized
      if (typeof imageIndex === 'undefined') {
        return
      }

      // Go to that variant image's slide
      if (this.flickity) {
        this.flickity.goToSlide(imageIndex)
      }
    }
  }

  stackedMediaInit() {
    const mediaGalleryElements = this.container.querySelectorAll('.product-slideshow .product-main-slide')

    this.mediaObservers = []

    for (let index = 0; index < mediaGalleryElements.length; index++) {
      const slideElement = mediaGalleryElements[index]
      const mediaObserver = new IntersectionObserver(
        (entries, observer) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
              this.settings.currentSlideIndex = index
              this.handleStackedMediaChange(this.settings.currentSlideIndex)
            }
          })
        },
        {
          root: null, // Use the viewport as the root
          rootMargin: '400px 0px 0px 0px', // Adjust the top offset to delay the intersection
          threshold: [0, 0.5, 1]
        }
      )

      mediaObserver.observe(slideElement)

      // Store mediaObserver instance in array
      this.mediaObservers.push(mediaObserver)
    }
  }

  handleStackedMediaChange(index) {
    const mediaTarget = this.container.querySelectorAll('.product-slideshow .product-main-slide')[index]

    if (!mediaTarget) return

    if (this.settings.hasVideos) {
      this.stopVideos()

      const video = mediaTarget.querySelector(this.selectors.productVideo)
      if (video) {
        const videoType = this._getVideoType(video)
        const videoId = this._getVideoDivId(video)
        if (videoType === 'youtube') {
          if (this.videoObjects[videoId].videoPlayer && this.videoObjects[videoId].options.style !== 'sound') {
            setTimeout(() => {
              this.videoObjects[videoId].videoPlayer.playVideo()
            }, 1000)
            return
          }
        } else if (videoType === 'mp4') {
          this.playMp4Video(videoId)
        }
      }
    }

    if (this.settings.hasModels) {
      const allMedia = this.container.querySelector(this.selectors.media)
      if (allMedia.length) {
        allMedia.forEach((el) => {
          /**
           * @event mediaHidden
           * @description Event fired when media is hidden.
           * @param {boolean} bubbles - Whether the event bubbles up through the DOM or not.
           * @param {boolean} cancelable - Whether the event is cancelable or not.
           */
          el.dispatchEvent(
            new CustomEvent('mediaHidden', {
              bubbles: true,
              cancelable: true
            })
          )
        })
      }

      const currentMedia = mediaTarget.querySelector(this.selectors.media)
      if (currentMedia) {
        currentMedia.dispatchEvent(
          /**
           * @event mediaVisible
           * @description Event fired when media is visible.
           * @param {object} detail - The detail object.
           * @param {boolean} detail.autoplayMedia - Whether the media should autoplay or not.
           * @param {boolean} bubbles - Whether the event bubbles up through the DOM or not.
           * @param {boolean} cancelable - Whether the event is cancelable or not.
           */
          new CustomEvent('mediaVisible', {
            bubbles: true,
            cancelable: true,
            detail: {
              autoplayMedia: false
            }
          })
        )
        mediaTarget.querySelector('.shopify-model-viewer-ui__button').setAttribute('tabindex', 0)
        mediaTarget.querySelector('.product-single__close-media').setAttribute('tabindex', 0)
      }
    }
  }

  scrollToStackedMedia(index) {
    const mediaTarget = this.container.querySelectorAll('.product-slideshow .product-main-slide')[index]

    if (!mediaTarget) return

    const position = mediaTarget.offsetTop

    window.scroll({
      top: position,
      behavior: 'smooth'
    })
  }

  /* @SH: added params for thumb groups to hijack imageSet logic */
  initProductSlider(variant, shThumbGroups, shThumbGroup) {
  /* /@SH */
    // Stop if only a single image, but add active class to first slide
    if (this.cache.mainSlider.querySelectorAll(this.selectors.slide).length <= 1) {
      const slide = this.cache.mainSlider.querySelector(this.selectors.slide)
      if (slide) {
        slide.classList.add('is-selected')
      }
      return
    }

    // Destroy slider in preparation of new initialization
    if (this.flickity && typeof this.flickity.destroy === 'function') {
      this.flickity.destroy()
    }

    // If variant argument exists, slideshow is reinitializing because of the
    // image set feature enabled and switching to a new group.
    // currentSlideIndex
    if (variant) {
      const activeSlide = this.cache.mainSlider.querySelector(this.selectors.startingSlide)
      this.settings.currentSlideIndex = this._slideIndex(activeSlide)
    }

    let mainSliderArgs = {
      dragThreshold: 25,
      adaptiveHeight: true,
      avoidReflow: true,
      initialIndex: this.settings.currentSlideIndex,
      childNav: this.cache.thumbSlider,
      childNavScroller: this.cache.thumbScroller,
      childVertical: this.cache.thumbSlider?.dataset.position === 'beside',
      /* @SH: Added extra argument */
      shChildNavArrowBehaviour: this.settings.shThumbsArrowBehaviour,
      prevNextButtons: this.dataset.arrows === 'true',
      /* /@SH */
      pageDots: true, // mobile only with CSS
      wrapAround: true,
      callbacks: {
        onInit: this.onSliderInit.bind(this),
        onChange: this.onSlideChange.bind(this)
      }
    }

    // Override default settings if image set feature enabled
    if (this.settings.imageSetName) {
      const imageSetArgs = this.imageSetArguments(variant)
      mainSliderArgs = Object.assign({}, mainSliderArgs, imageSetArgs)
      this.updateImageSetThumbs(mainSliderArgs.imageSet)
    }

    /* @SH: Filter images for thumbgroups */
    if (shThumbGroups) {
      const imageSetArgs = {
        cellSelector: '[data-thumb-group="' + shThumbGroup + '"]',
        imageSet: shThumbGroup,
        initialIndex: this.settings.currentSlideIndex
      }
      mainSliderArgs = Object.assign({}, mainSliderArgs, imageSetArgs)
      this.updateImageSetThumbs(mainSliderArgs.imageSet, shThumbGroups)
    }
    /* /@SH */

    if (!config.bpSmall && this.settings.mediaGalleryLayout === 'stacked') {
      const imageContainer = this.container.querySelector(this.selectors.imageContainer) || this
      imageContainer.setAttribute('data-has-slideshow', 'false')

      this.stackedMediaInit()
      return
    }

    this.flickity = new Slideshow(this.cache.mainSlider, mainSliderArgs)

    // Ensure we resize the slider to avoid reflow issues
    setTimeout(() => {
      this.flickity.resize()
    }, 100)
  }

  onSliderInit(slide) {
    // If slider is initialized with image set feature active,
    // initialize any videos/media when they are first slide
    if (this.settings.imageSetName) {
      this.prepMediaOnSlide(slide)
    }

    /* @SH: Handling for mobile sticky image */
    if (this.settings.shMobileStickyImage || true) {
      this.shMobileImagesHeight = document.querySelector('.product__main-photos').getBoundingClientRect().height;
      this.shDebounceSetMobileStickyImage = debounce(500, this.shSetMobileStickyImage.bind(this));
      window.addEventListener('resize', this.shDebounceSetMobileStickyImage);
      this.shSetMobileStickyImage();
    }
    /* /@SH */
  }

  onSlideChange(index) {
    if (!this.flickity) return

    const prevSlide = this.cache.mainSlider.querySelector(
      '.product-main-slide[data-index="' + this.settings.currentSlideIndex + '"]'
    )

    // If imageSetName exists, use a more specific selector
    /* @SH: Added condition for thumbgroup */
    const nextSlide = this.settings.imageSetName || this.settings.shThumbGroup
    /* /@SH */
      ? this.cache.mainSlider.querySelectorAll('.flickity-slider .product-main-slide')[index]
      : this.cache.mainSlider.querySelector('.product-main-slide[data-index="' + index + '"]')

    /* @SH: Added in checks for prev and next slides */
    prevSlide?.setAttribute('tabindex', '-1')
    nextSlide?.setAttribute('tabindex', 0)

    // Pause any existing slide video/media
    if(prevSlide) this.stopMediaOnSlide(prevSlide)

    // Prep next slide video/media
    if(nextSlide) this.prepMediaOnSlide(nextSlide)
    /* /@SH */

    // Update current slider index
    this.settings.currentSlideIndex = index
  }

  stopMediaOnSlide(slide) {
    // Stop existing video
    const video = slide.querySelector(this.selectors.productVideo)
    if (video) {
      const videoType = this._getVideoType(video)
      const videoId = this._getVideoDivId(video)
      if (videoType === 'youtube') {
        if (this.videoObjects[videoId].videoPlayer) {
          this.videoObjects[videoId].videoPlayer.stopVideo()
          return
        }
      } else if (videoType === 'mp4') {
        this.stopMp4Video(videoId)
        return
      }
    }

    // Stop existing media
    const currentMedia = slide.querySelector(this.selectors.media)
    if (currentMedia) {
      currentMedia.dispatchEvent(
        new CustomEvent('mediaHidden', {
          bubbles: true,
          cancelable: true
        })
      )
    }
  }

  prepMediaOnSlide(slide) {
    const video = slide.querySelector(this.selectors.productVideo)
    if (video) {
      this.flickity.reposition()
      const videoType = this._getVideoType(video)
      const videoId = this._getVideoDivId(video)
      if (videoType === 'youtube') {
        if (this.videoObjects[videoId].videoPlayer && this.videoObjects[videoId].options.style !== 'sound') {
          this.videoObjects[videoId].videoPlayer.playVideo()
          return
        }
      } else if (videoType === 'mp4') {
        this.playMp4Video(videoId)
      }
    }

    const nextMedia = slide.querySelector(this.selectors.media)
    if (nextMedia) {
      nextMedia.dispatchEvent(
        new CustomEvent('mediaVisible', {
          bubbles: true,
          cancelable: true,
          detail: {
            autoplayMedia: true
          }
        })
      )
      slide.querySelector('.shopify-model-viewer-ui__button').setAttribute('tabindex', 0)
      slide.querySelector('.product-single__close-media').setAttribute('tabindex', 0)
    }
  }

  _slideIndex(el) {
    return el.getAttribute('data-index')
  }

  /*============================================================================
    Product videos
  ==============================================================================*/
  videoSetup() {
    const productVideos = this.cache.mainSlider.querySelectorAll(this.selectors.productVideo)

    if (!productVideos.length) {
      return false
    }

    productVideos.forEach((vid) => {
      const type = vid.dataset.videoType
      if (type === 'youtube') {
        this.initYoutubeVideo(vid)
      } else if (type === 'vimeo') {
        this.initVimeoVideo(vid)
      } else if (type === 'mp4') {
        this.initMp4Video(vid)
      }
    })
  }

  initYoutubeVideo(div) {
    this.videoObjects[div.id] = new YouTube(div.id, {
      videoId: div.dataset.videoId,
      videoParent: this.selectors.videoParent,
      autoplay: false, // will handle this in callback
      style: div.dataset.videoStyle,
      loop: div.dataset.videoLoop,
      controls: this.dataset.videoControls === 'true' ? 1 : 0,
      events: {
        onReady: this.youtubePlayerReady.bind(this),
        onStateChange: this.youtubePlayerStateChange.bind(this)
      }
    })
  }

  initVimeoVideo(div) {
    this.videoObjects[div.id] = new VimeoPlayer(div.id, div.dataset.videoId, {
      videoParent: this.selectors.videoParent,
      autoplay: false,
      style: div.dataset.videoStyle,
      loop: div.dataset.videoLoop
    })
  }

  // Comes from YouTube SDK
  // Get iframe ID with evt.target.getIframe().id
  // Then access product video players with this.videoObjects[id]
  youtubePlayerReady(evt) {
    const iframeId = evt.target.getIframe().id

    if (!this.videoObjects[iframeId]) {
      // No youtube player data
      return
    }

    const obj = this.videoObjects[iframeId]
    const player = obj.videoPlayer

    if (obj.options.style !== 'sound') {
      player.mute()
    }

    obj.parent.classList.remove('loading')
    obj.parent.classList.add('loaded')
    obj.parent.classList.add('video-interactable') // Previously, video was only interactable after slide change

    // If we have an element, it is in the visible/first slide,
    // and is muted, play it
    if (this._isFirstSlide(iframeId) && obj.options.style !== 'sound') {
      player.playVideo()
    }
  }

  _isFirstSlide(id) {
    return this.cache.mainSlider.querySelector(this.selectors.startingSlide + ' ' + '#' + id)
  }

  youtubePlayerStateChange(evt) {
    const iframeId = evt.target.getIframe().id
    const obj = this.videoObjects[iframeId]

    switch (evt.data) {
      case -1: // unstarted
        // Handle low power state on iOS by checking if
        // video is reset to unplayed after attempting to buffer
        if (obj.attemptedToPlay) {
          obj.parent.classList.add('video-interactable')
        }
        break
      case 0: // ended
        if (obj && obj.options.loop === 'true') {
          obj.videoPlayer.playVideo()
        }
        break
      case 3: // buffering
        obj.attemptedToPlay = true
        break
    }
  }

  initMp4Video(div) {
    this.videoObjects[div.id] = {
      id: div.id,
      type: 'mp4'
    }

    if (this._isFirstSlide(div.id)) {
      this.playMp4Video(div.id)
    }

    /* @SH: Add click to play pause for overlay on video to allow for swipability */
    div.parentNode.addEventListener('click', (e) => {
      if (div.paused) {
        this.playMp4Video(div.id)
      } else {
        // is playing
        this.stopMp4Video(div.id)
      }
    });
    /* /@SH */
  }

  stopVideos() {
    for (const [id, vid] of Object.entries(this.videoObjects)) {
      if (vid.videoPlayer) {
        if (typeof vid.videoPlayer.stopVideo === 'function') {
          vid.videoPlayer.stopVideo() // YouTube player
        }
      } else if (vid.type === 'mp4') {
        this.stopMp4Video(vid.id) // MP4 player
      }
    }
  }

  _getVideoType(video) {
    return video.getAttribute('data-video-type')
  }

  _getVideoDivId(video) {
    return video.id
  }

  playMp4Video(id) {
    const player = this.container.querySelector('#' + id)
    const playPromise = player.play()

    player.setAttribute('controls', '')
    player.focus()

    // When existing focus on the element, go back to thumbnail
    player.addEventListener('focusout', this.returnFocusToThumbnail.bind(this))

    if (playPromise !== undefined) {
      playPromise
        .then(function () {
          // Playing as expected
        })
        .catch(function (error) {
          // Likely low power mode on iOS, show controls
          player.setAttribute('controls', '')
          player.closest(this.selectors.videoParent).setAttribute('data-video-style', 'unmuted')
        })
    }
  }

  stopMp4Video(id) {
    const player = this.container.querySelector('#' + id)
    if (!player) return
    player.removeEventListener('focusout', this.returnFocusToThumbnail.bind(this))
    if (typeof player.pause === 'function') {
      player.removeAttribute('controls')
      player.pause()
    }
  }

  returnFocusToThumbnail(evt) {
    // Only return focus to active thumbnail if relatedTarget
    // is a thumbnail, otherwise user may have clicked elsewhere on the page
    if (evt.relatedTarget && evt.relatedTarget.classList.contains('product__thumb')) {
      const thumb = this.container.querySelector(
        '.product__thumb-item[data-index="' + this.settings.currentSlideIndex + '"] a'
      )
      if (thumb) {
        thumb.focus()
      }
    }
  }

  /*============================================================================
    Product media (3D)
  ==============================================================================*/
  initModelViewerLibraries() {
    const modelViewerElements = this.container.querySelectorAll(this.selectors.media)
    if (modelViewerElements.length < 1) return

    init(modelViewerElements, this.sectionId)
  }

  customMediaListeners() {
    document.querySelectorAll(this.selectors.closeMedia).forEach((el) => {
      el.addEventListener(
        'click',
        function () {
          let slide

          if (this.settings.mediaGalleryLayout === 'stacked') {
            slide = this.cache.mainSlider.querySelector(
              `.product-main-slide[data-index="${this.settings.currentSlideIndex}"]`
            )
          } else {
            slide = this.cache.mainSlider.querySelector(this.selectors.currentSlide)
          }

          const media = slide.querySelector(this.selectors.media)
          if (media) {
            media.dispatchEvent(
              new CustomEvent('mediaHidden', {
                bubbles: true,
                cancelable: true
              })
            )
          }
        }.bind(this)
      )
    })

    const modelViewers = this.container.querySelectorAll('model-viewer')
    if (modelViewers.length) {
      modelViewers.forEach((el) => {
        el.addEventListener(
          'shopify_model_viewer_ui_toggle_play',
          function (evt) {
            this.mediaLoaded(evt)
          }.bind(this)
        )

        el.addEventListener(
          'shopify_model_viewer_ui_toggle_pause',
          function (evt) {
            this.mediaUnloaded(evt)
          }.bind(this)
        )
      })
    }
  }

  mediaLoaded(evt) {
    this.container.querySelectorAll(this.selectors.closeMedia).forEach((el) => {
      el.classList.remove(this.classes.hidden)
    })

    if (this.flickity) {
      this.flickity.setDraggable(false)
    }
  }

  mediaUnloaded(evt) {
    this.container.querySelectorAll(this.selectors.closeMedia).forEach((el) => {
      el.classList.add(this.classes.hidden)
    })

    if (this.flickity) {
      this.flickity.setDraggable(true)
    }
  }

  /* @SH: Handling for thumb groups click and filtering */
  shOnThumbGroup(e) {
    this.flickity.goToSlide(0);
    this.settings.shThumbGroup = e.detail.thumbGroup;
    this.updateImageSet(null, true, e.detail.thumbGroup);
  }
  /* /@SH */
}

customElements.define('product-images', ProductImages)
